import Button from '@atlaskit/button';
import LoadingButton from '@atlaskit/button/loading-button';
import InlineDialog from '@atlaskit/inline-dialog';
import * as React from 'react';
import { useReducer } from 'react';
import { analytics } from '@atlassian/temp-analytics';

import './internal-app-switcher.scss';
import { fetchExternalAppLinks } from '../../../utils/proxies/src/switcher/switcher';
import { AppSwitcherEntity, ExternalAppLink, ExternalAppSource } from '@atlassian/cssxp-typescommon';

enum ActionType {
  FETCH_EXTERNAL_APP_LINKS = 'FETCH_EXTERNAL_APP_LINKS',
  FETCH_EXTERNAL_APP_LINKS_SUCCEEDED = 'FETCH_EXTERNAL_APP_LINKS_SUCCEEDED',
  FETCH_EXTERNAL_APP_LINKS_FAILED = 'FETCH_EXTERNAL_APP_LINKS_FAILED',
}

interface State {
  isFetching: boolean;
  error: any;
  successful: boolean;
  links: ExternalAppLink[];
}

interface Action {
  type: ActionType;
  links?: ExternalAppLink[];
  error?: string | null;
}

const initialState: State = {
  isFetching: false,
  error: null,
  successful: false,
  links: [],
};

const reducer = (state: State, action: Action) => {
  switch (action.type) {
    case ActionType.FETCH_EXTERNAL_APP_LINKS:
      return {
        isFetching: true,
        error: null,
        successful: false,
        links: [] as ExternalAppLink[],
      };
    case ActionType.FETCH_EXTERNAL_APP_LINKS_SUCCEEDED:
      return {
        isFetching: false,
        error: null,
        successful: true,
        links: action.links || [],
      };
    case ActionType.FETCH_EXTERNAL_APP_LINKS_FAILED:
      return {
        isFetching: false,
        error: action.error,
        successful: false,
        links: [] as ExternalAppLink[],
      };
    default:
      return state;
  }
};

interface InternalAppSwitcherProps {
  entity: AppSwitcherEntity;
  id: string;
  sourceSystem?: ExternalAppSource;
}

// TODO : Replace with UI Event Constants
const sendAnalyticsEvent = (entity: AppSwitcherEntity, app: ExternalAppSource) => {
  analytics.sendUIEvent({
    action: 'clicked',
    actionSubject: 'button',
    actionSubjectId: 'appSwitcherLinkButton',
    source: 'unknownScreen',
    tags: ['appSwitcher', 'redirect'],
    attributes: {
      entity,
      app,
    },
  });
};

const InternalAppSwitcher = (props: InternalAppSwitcherProps) => {
  const [state, dispatch] = useReducer(reducer, initialState);
  const { entity, id, sourceSystem } = props;
  const [isSwitcherOpen, setSwitcherOpen] = React.useState(false);

  React.useEffect(() => {
    let isSubscribed = true;

    const fetchData = async () => {
      dispatch({
        type: ActionType.FETCH_EXTERNAL_APP_LINKS,
      });

      try {
        const links = await fetchExternalAppLinks(entity, id, sourceSystem);

        if (isSubscribed) {
          dispatch({
            type: ActionType.FETCH_EXTERNAL_APP_LINKS_SUCCEEDED,
            links,
          });
        }
      } catch (error: any) {
        if (isSubscribed) {
          dispatch({
            type: ActionType.FETCH_EXTERNAL_APP_LINKS_FAILED,
            error: error.message,
          });
        }
      }
    };

    fetchData();

    return () => {
      isSubscribed = false;
    };
  }, [id, entity, sourceSystem]);

  const { links, isFetching } = state;

  const handleAppSwitcherClick = () => {
    setSwitcherOpen(true);
  };

  if (links.length === 1) {
    const viewInLabel = `View in ${links[0].name}`;
    const viewInHref = links[0].url;
    return (
      <Button
        onClick={() => sendAnalyticsEvent(entity, links[0].name)}
        href={viewInHref}
        target="_blank"
        testId="view-button"
      >
        {viewInLabel}
      </Button>
    );
  }

  const noExternalLinks = links.length === 0;
  return (
    <InlineDialog
      onClose={() => setSwitcherOpen(false)}
      isOpen={isSwitcherOpen}
      content={
        <div
          style={{
            maxHeight: '448px',
            maxWidth: '448px',
            overflow: 'auto',
          }}
        >
          {links.map((link: ExternalAppLink) => (
            <AppLink
              key={link.name}
              label={link.name}
              href={link.url}
              entity={entity}
              onClick={() => setSwitcherOpen(false)}
            />
          ))}
        </div>
      }
    >
      <LoadingButton
        isLoading={isFetching}
        onClick={handleAppSwitcherClick}
        isDisabled={noExternalLinks}
        target="_blank"
        testId="view-button"
      >
        View in...
      </LoadingButton>
    </InlineDialog>
  );
};

interface AppLinkProps {
  label: ExternalAppSource;
  href: string;
  entity: AppSwitcherEntity;
  onClick: () => void;
}

const AppLink = (props: AppLinkProps) => {
  const { href, label, entity, onClick } = props;

  return (
    <span
      data-testid={`app-link-${label}`}
      onClick={() => {
        sendAnalyticsEvent(entity, label);
        onClick();
      }}
    >
      <a href={href} target="_blank" className={'app-link'}>
        <span>{label}</span>
      </a>
    </span>
  );
};

export default InternalAppSwitcher;
